<template>
  <div class="contract-card box" :class="statusClass">
    <div class="contract-card__header">
      <div class="contract-card__identity">
        <span class="contract-card__avatar">{{ initials }}</span>
        <div>
          <p class="contract-card__company">{{ item.organismo_nombre || item.empresa_nombre || item.empresa }}</p>
          <p class="contract-card__folio">
            {{ item.folio }}
            <span v-if="item.dependencias_count" class="has-text-grey">
              · {{ item.dependencias_count }} dependencia(s)
            </span>
            <span v-if="item.metrics.ampliacion_presupuesto_activa || item.metrics.ampliacion_vigencia_activa" class="has-text-grey">
              · ampliación {{ extensionPercent }}% aplicada
            </span>
          </p>
        </div>
      </div>
      <span class="tag" :class="statusTagClass">{{ statusLabel }}</span>
    </div>

    <div class="contract-card__dates">
      <span>{{ formatDate(item.fecha_inicio) }} – {{ formatDate(item.fecha_termino_efectiva) }}</span>
      <span :class="vigenciaDaysClass">{{ item.metrics.dias_restantes }} días restantes</span>
    </div>

    <progress class="progress" :class="presupuestoProgressClass" :value="item.metrics.porcentaje_consumido" max="100"></progress>

    <div class="contract-card__amounts">
      <span>
        {{ formatMoney(item.metrics.consumo) }} de {{ formatMoney(item.presupuesto_maximo_efectivo) }} consumido
      </span>
      <strong>{{ item.metrics.porcentaje_consumido }}%</strong>
    </div>

    <p v-if="item.presupuesto_minimo > 0" class="is-size-7 has-text-grey">
      Presupuesto mínimo (informativo): {{ formatMoney(item.presupuesto_minimo) }}
    </p>

    <p v-if="!item.vigente && item.causal_estatus" class="is-size-7 has-text-danger">
      {{ item.causal_estatus }}
    </p>

    <div class="contract-card__footer">
      <div class="contract-card__badges">
        <span class="contract-card__badges-title">Ampliaciones:</span>
        <span class="tag is-light">{{ item.metrics.ampliaciones_vigencia }} vigencia</span>
        <span class="tag is-light">{{ item.metrics.ampliaciones_presupuesto }} presupuesto</span>
      </div>
      <div class="contract-card__actions">
        <router-link :to="{ name: 'budget-control-show', params: { id: item.id } }" class="button is-small is-light" title="Ver detalle">
          <b-icon icon="eye" pack="fas" size="is-small"></b-icon>
        </router-link>
        <button
          v-if="canCancel"
          class="button is-small is-light"
          title="Cancelar contrato"
          @click="$emit('cancel', item)"
        >
          <b-icon icon="ban" pack="fas" size="is-small"></b-icon>
        </button>
        <button
          v-if="canAmpliacion"
          class="button is-small"
          :class="needsAmpliacion ? 'is-warning' : 'is-light'"
          title="Autorizar ampliación"
          @click="$emit('ampliacion', item)"
        >
          <b-icon icon="file-signature" pack="fas" size="is-small"></b-icon>
        </button>
        <button
          v-if="showSolicitarAmpliacion"
          class="button is-small is-warning"
          title="Solicitar autorización por correo"
          @click="$emit('solicitar-ampliacion', item)"
        >
          <b-icon icon="envelope" pack="fas" size="is-small"></b-icon>
        </button>
        <button
          v-if="needsAmpliacion && canAmpliacion"
          class="button is-small is-warning is-outlined"
          @click="$emit('ampliacion', item)"
        >
          Autorizar ampliación
        </button>
        <button
          v-if="showSolicitarAmpliacion"
          class="button is-small is-warning is-outlined"
          @click="$emit('solicitar-ampliacion', item)"
        >
          Solicitar autorización
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { needsAnyExtension } from './extensionNeeds';

const STATUS_LABELS = {
  vigente: 'Vigente',
  por_agotarse: 'Por agotarse',
  cerca_limite: 'Cerca del límite',
  agotado: 'Agotado',
  proximo_finalizar: 'Próximo a finalizar',
  a_dias_finalizar: 'A días de finalizar',
  no_vigente: 'No vigente'
};

const ALERT_TAG_CLASS = {
  amarillo: 'bc-tag--amarillo',
  naranja: 'bc-tag--naranja',
  rojo: 'bc-tag--rojo',
  verde: 'bc-tag--verde',
  gris: 'bc-tag--gris'
};

const SEMAFORO_PRIORITY = {
  amarillo: 1,
  naranja: 2,
  rojo: 3
};

function worstSemaphoreAlert(presupuesto, vigencia) {
  const alerts = [presupuesto, vigencia].filter(Boolean);
  if (!alerts.length) {
    return null;
  }

  return alerts.reduce((worst, current) => (
    (SEMAFORO_PRIORITY[current] || 0) > (SEMAFORO_PRIORITY[worst] || 0) ? current : worst
  ));
}
const STATUS_SEMAFORO_CLASS = {
  vigente: ALERT_TAG_CLASS.verde,
  por_agotarse: ALERT_TAG_CLASS.amarillo,
  cerca_limite: ALERT_TAG_CLASS.naranja,
  agotado: ALERT_TAG_CLASS.rojo,
  proximo_finalizar: ALERT_TAG_CLASS.naranja,
  a_dias_finalizar: ALERT_TAG_CLASS.rojo,
  no_vigente: ALERT_TAG_CLASS.gris
};

const STATUS_PROGRESS_CLASS = {
  vigente: 'is-success',
  por_agotarse: 'bc-progress--amarillo',
  cerca_limite: 'bc-progress--naranja',
  agotado: 'bc-progress--rojo',
  proximo_finalizar: 'is-success',
  a_dias_finalizar: 'is-success',
  no_vigente: 'is-dark'
};

export default {
  name: 'ContractListItem',
  props: {
    item: { type: Object, required: true },
    canCancel: { type: Boolean, default: false },
    canAmpliacion: { type: Boolean, default: false },
    canSolicitarAmpliacion: { type: Boolean, default: false }
  },
  computed: {
    initials() {
      const name = this.item.organismo_nombre || this.item.empresa_nombre || this.item.empresa || '';
      return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || 'EM';
    },
    statusKey() {
      return this.item.metrics?.estatus_label || 'no_vigente';
    },
    statusLabel() {
      const key = `budget-control.status.${this.statusKey}`;
      const translated = this.$t(key);
      return translated === key ? (STATUS_LABELS[this.statusKey] || this.statusKey) : translated;
    },
    statusClass() {
      return `contract-card--${this.statusKey}`;
    },
    semaphoreAlert() {
      return worstSemaphoreAlert(
        this.item.metrics?.alerta_presupuesto,
        this.item.metrics?.alerta_vigencia
      );
    },
    isAtMaxLimit() {
      const metrics = this.item.metrics || {};
      return metrics.alerta_presupuesto === 'rojo' || metrics.alerta_vigencia === 'rojo';
    },
    statusTagClass() {
      if (this.isAtMaxLimit) {
        return 'bc-tag--rojo-max';
      }

      if (this.semaphoreAlert) {
        return ALERT_TAG_CLASS[this.semaphoreAlert];
      }

      return STATUS_SEMAFORO_CLASS[this.statusKey] || ALERT_TAG_CLASS.verde;
    },
    presupuestoProgressClass() {
      const alerta = this.item.metrics?.alerta_presupuesto;
      if (alerta) {
        return `bc-progress--${alerta}`;
      }
      return STATUS_PROGRESS_CLASS[this.statusKey] || 'is-success';
    },
    vigenciaDaysClass() {
      const alerta = this.item.metrics?.alerta_vigencia;
      if (alerta) {
        return `bc-text--${alerta}`;
      }
      return 'has-text-grey';
    },
    needsAmpliacion() {
      return needsAnyExtension(this.item);
    },
    showSolicitarAmpliacion() {
      return this.canSolicitarAmpliacion && this.needsAmpliacion;
    },
    extensionPercent() {
      return 20;
    }
  },
  methods: {
    formatMoney(value) {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value || 0);
    },
    formatDate(value) {
      if (!value) return '';
      const date = new Date(value + 'T00:00:00');
      return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }
};
</script>

<style scoped>
.contract-card {
  margin-bottom: 1rem;
}
.contract-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.75rem;
}
.contract-card__identity {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}
.contract-card__avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #f0f0f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #555;
}
.contract-card__company {
  font-weight: 700;
  margin-bottom: 0.15rem;
}
.contract-card__folio {
  font-size: 0.9rem;
  color: #666;
}
.contract-card__dates {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}
.contract-card__amounts {
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.contract-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 0.75rem;
}
.contract-card__badges {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}
.contract-card__badges-title {
  margin: 0;
  font-size: 0.625rem;
  font-weight: 700;
  color: #7a7a7a;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.contract-card__actions {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}
</style>
